import { Injectable, signal, inject } from '@angular/core';
import {
  OpenQuoteRequestsItem,
} from '@sbd-account-portal/app/modules/dashboard/open-quote-requests-widget/quote-requests.types';
import { FormArray, FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PaymentResponseData } from '@sbd-account-portal/app/modules/payment/payment.types';
import { User } from '@sbd-account-portal/app/core/user/user.types';
import {
  OpenQuoteRequestsWidgetService,
} from '@sbd-account-portal/app/modules/dashboard/open-quote-requests-widget/open-quote-requests-widget.service';
import { map, Observable } from 'rxjs';
import { PaymentService } from '@sbd-account-portal/app/modules/payment/payment.service';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import { validateCreditCard } from '@sbd-doug-frontend/shared/lib/public-validators';

@Injectable({
  providedIn: 'root',
})
export class MainFuseDrawerService {
  private _openQuoteRequestsWidgetService = inject(OpenQuoteRequestsWidgetService);
  private _formBuilder = inject(FormBuilder);
  private _paymentService = inject(PaymentService);
  private _cathyService = inject(CathyService);

  openDrawer = signal<boolean>(false);
  contentContext = signal<string>(null);

  // Purchase steps data
  step = signal<number>(1);
  selectedQR = signal<OpenQuoteRequestsItem>(null);
  queuedQR = signal<OpenQuoteRequestsItem[]>([]);
  subtotal = signal<number>(0);
  shippingTotal = signal<number>(0);
  total = signal<number>(0);
  isPurchasing = signal<boolean>(false);
  activeOpenedQR = signal<any>({});
  checkoutForm = signal<UntypedFormGroup>(null);
  paymentResponseData = signal<PaymentResponseData|null>(null);
  loggedInAccountUser = signal<User>(null);
  isPurchased = signal<boolean>(false);
  showStepsHeader = signal<boolean>(true);
  riderSubmitted = signal<boolean>(false);
  recentlyPurchasedCount = signal<number>(0);


  constructor() {
  }

  initializePurchaseSteps(data: {
    step: number;
    selectedQR: OpenQuoteRequestsItem;
    queuedQR: OpenQuoteRequestsItem[];
    subtotal: number;
    shippingTotal: number;
    total: number;
    isPurchasing: boolean;
    activeOpenedQR: any;
    checkoutForm: UntypedFormGroup;
    paymentResponseData: PaymentResponseData|null;
    loggedInAccountUser: User;
    isPurchased: boolean;
    recentlyPurchasedCount: number;
  }) {
    this.step.set(data.step);
    this.selectedQR.set(data.selectedQR);
    this.queuedQR.set(data.queuedQR);
    this.subtotal.set(data.subtotal);
    this.shippingTotal.set(data.shippingTotal);
    this.total.set(data.total);
    this.isPurchasing.set(data.isPurchasing);
    this.activeOpenedQR.set(data.activeOpenedQR);
    this.checkoutForm.set(data.checkoutForm);
    this.paymentResponseData.set(data.paymentResponseData);
    this.loggedInAccountUser.set(data.loggedInAccountUser);
    this.isPurchased.set(data.isPurchased);
    this.recentlyPurchasedCount.set(data.recentlyPurchasedCount);
  }

  resetPurchaseSteps() {
    this.step.set(1);
    this.selectedQR.set(null);
    this.queuedQR.set([]);
    this.subtotal.set(0);
    this.shippingTotal.set(0);
    this.total.set(0);
    this.isPurchasing.set(false);
    this.activeOpenedQR.set({});
    this.paymentResponseData.set(null);
    this.isPurchased.set(false);
    this.recentlyPurchasedCount.set(0);
  }

  calculateSubTotal() {
    const currentQueuedQR = this.queuedQR();
    const calculatedSubtotal = currentQueuedQR.filter(c => +c.customer_can_purchase === 1).reduce((acc, bond) => acc + bond.selected_quote?.premium, 0);
    this.subtotal.set(calculatedSubtotal);
  }

  addOpenQRToQueue(qr: OpenQuoteRequestsItem): void {
    const currentQueuedQR = this.queuedQR();
    currentQueuedQR.push(qr);
    this.queuedQR.set(currentQueuedQR);
  }

  updateQuoteSelectionForms(): void {
    const currentQueuedQR = this.queuedQR();
    currentQueuedQR.forEach(quote => {
      quote.quote_selection_form = this._formBuilder.group({
        selected_quote_option: [quote.selected_quote?.premium ?? 0],
      });
    });
    this.queuedQR.set(currentQueuedQR);
  }

  updateSelectedQuoteOption(event: any): Observable<any> {
    const updatedQR = event;
    const quoteId = updatedQR.selected_quote.quote_id;

    return this._openQuoteRequestsWidgetService.setSelectedQuoteOption(+updatedQR.quote_request_id, quoteId).pipe(
      map((r) => {
        updatedQR.quote_request_bond.selected_quote_id = updatedQR.selected_quote.quote_id;

        const currentQueuedQR = this.queuedQR();
        const updatedQueuedQR = currentQueuedQR.map((quote) =>
          quote.quote_request_id === updatedQR.quote_request_id ? updatedQR : quote,
        );

        this.queuedQR.set(updatedQueuedQR);
        this.calculateSubTotal();

        return r;
      }),
    );
  }

  reArrangeQR() {
    let quoteRequestIDs = [];
    const currentQueuedQR = this.queuedQR();
    const currentCheckoutForm = this.checkoutForm();

    // Group quote requests by shipping option label
    const groupedQR = currentQueuedQR.reduce((grouped, item) => {
      const key = item.shipping_option_label;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
      return grouped;
    }, {});

    this.activeOpenedQR.set(groupedQR);

    Object.keys(groupedQR).forEach((key) => {
      const groupFields = groupedQR[key].map((item: any) =>
        this._formBuilder.group({
          shipping_option_label: [item.shipping_option_label],
          shipping_method: [''],
          quote_request_ids: [groupedQR[key].map(t => t.quote_request_id)],
        }),
      );
      const control = this._formBuilder.array(groupFields);
      const n_key = key.toString().toLowerCase().replace(/ /g, '_');
      currentCheckoutForm?.addControl(`group_${n_key}`, control);
    });

    let calculatedShippingTotal = 0;
    currentQueuedQR.forEach((item) => {
      const shippingPrice = Number.parseInt(String(item.shipping_option_price));
      calculatedShippingTotal += isNaN(shippingPrice) ? 0 : shippingPrice;
      quoteRequestIDs.push(item.quote_request_id);
    });


    //Shipping + Bond Charges
    this.calculateSubTotal();
    this.shippingTotal.set(calculatedShippingTotal);
    const currentSubtotal = this.subtotal();
    this.total.set(currentSubtotal + calculatedShippingTotal);

    // Update checkout from with quote request IDs
    currentCheckoutForm?.get('quoteRequestIDs').setValue(quoteRequestIDs);
    this.checkoutForm.set(currentCheckoutForm);
  }

  purchaseQuoteRequest(): Observable<any> {
    const currentCheckoutForm = this.checkoutForm();
    const currentQueuedQR = this.queuedQR();

    this.isPurchasing.set(true);

    return this._paymentService.purchaseQuoteRequest(currentCheckoutForm.value).pipe(
      map((r) => {
        if (r.success) {
          this.reArrangeQR();
          this._cathyService.success(r.message);
          this.paymentResponseData.set(r.data);


          // Update signals for success state
          this.recentlyPurchasedCount.set(currentQueuedQR.length);
          this.queuedQR.set([]);
          this.activeOpenedQR.set({});
          this.total.set(this.paymentResponseData().transaction.amount);
          this.shippingTotal.set(0);
          this.subtotal.set(0);
          this.isPurchased.set(true);
          this.selectedQR.set(null);

          // Reset the checkout form
          currentCheckoutForm.reset();
          this.checkoutForm.set(currentCheckoutForm);

          this.step.set(5);
          // Refresh the open quote requests
          this._openQuoteRequestsWidgetService.getOpenQuoteRequests().subscribe();
        } else {
          this._cathyService.error(r.message ?? 'Something went wrong.');
        }

        this.isPurchasing.set(false);
        return r;
      }),
    );
  }

  currentOpenQRIsPurchasable() {
    return +this.selectedQR()?.customer_can_purchase === 1;
  }

  currentOpenQRIsPurchased() {
    return this.selectedQR()?.quote_status_stage === 'Issued';
  }


// Function to validate card number and set the card type
  validateCardNumber(checkoutForm: UntypedFormGroup, control: any): { [key: string]: boolean } | null {
    const value = control.value;
    if (!value) {
      checkoutForm?.get('selectedCardType')?.setValue('');
      return null;
    }

    // Regex patterns for card types
    const visaRegex = /^4[0-9]{12}(?:[0-9]{3})?$/;
    const mastercardRegex = /^5[1-5][0-9]{14}$/;
    const amexRegex = /^3[47][0-9]{13}$/;

    if (visaRegex.test(value)) {
      checkoutForm.get('selectedCardType')?.setValue('visa');
      return null;
    } else if (mastercardRegex.test(value)) {
      checkoutForm.get('selectedCardType')?.setValue('mastercard');
      return null;
    } else if (amexRegex.test(value)) {
      checkoutForm.get('selectedCardType')?.setValue('amex');
      return null;
    } else {
      checkoutForm.get('selectedCardType')?.setValue(''); // No valid card type
      return { invalidCard: true };
    }
  }
}
